import type { ComponentType, SVGProps } from 'react';
import type { ButtonProps } from '@immo/ui-kit';
import type { FleetCommandVerb, FleetHostCommandVerb } from '@immo/api-contract';
import { BoltIcon, FlowIcon, LinkIcon, RefreshIcon, TerminalIcon, TrashIcon, XIcon } from '@/components/layout/icons';
import { useTranslations } from '@/i18n/use-translations';

/** Every verb that confirms — `logs` and `logs-download` never do (IMM-910.8 Decisions). */
export const CONFIRMABLE_VERBS = [
  'start',
  'stop',
  'restart',
  'sync-branch',
  'clean-checkout',
  'restart-agent',
  'update-agent',
  'force-update-agent',
  'start-all',
  'stop-all',
  'restart-all',
  'logs-reset',
  'engine',
  'system-prune',
] as const;
export type ConfirmableVerb = (typeof CONFIRMABLE_VERBS)[number];

/** The verbs the machine panel issues for the whole host: the host verbs, and `system-prune` (fleet-agent ADR 0018). */
export type MachineCommandVerb = FleetHostCommandVerb | 'system-prune';

/**
 * The host verbs whose dialog is a form that then follows the command until it settles, rather than a confirm:
 * `run-command` (fleet-agent ADR 0016), `e2e-unlock` (IMM-1153) and `system-preview` (fleet-agent ADR 0018).
 */
export type FormCommandVerb = 'run-command' | 'e2e-unlock' | 'system-preview';

type VerbButtonVariant = NonNullable<ButtonProps['variant']>;

type VerbEntry = {
  readonly icon: ComponentType<SVGProps<SVGSVGElement>>;
  readonly variant: VerbButtonVariant;
};

const VERB_TABLE: Record<ConfirmableVerb, VerbEntry> = {
  // One lane (fleet-agent ADR 0013). `stop` is `danger` like `stop-all`: the
  // lane takes no work until someone starts it again, across reboots too.
  start: { icon: BoltIcon, variant: 'secondary' },
  stop: { icon: XIcon, variant: 'danger' },
  // Not `danger`: a round in flight ends at a step boundary and resumes.
  restart: { icon: RefreshIcon, variant: 'secondary' },
  'sync-branch': { icon: LinkIcon, variant: 'secondary' },
  // `danger`, like `stop-all`: it is the one verb on this page that deletes work.
  'clean-checkout': { icon: TrashIcon, variant: 'danger' },
  // Not `danger`: it interrupts the watcher, never a round or the checkout.
  'restart-agent': { icon: BoltIcon, variant: 'secondary' },
  // Not `danger`: it restarts the watcher like `restart-agent`, never a round.
  'update-agent': { icon: RefreshIcon, variant: 'secondary' },
  // `danger`, like `clean-checkout`: it discards whatever the agent's clone held.
  'force-update-agent': { icon: RefreshIcon, variant: 'danger' },
  // `stop-all` is `danger`: it aborts every build in flight on the machine.
  'start-all': { icon: BoltIcon, variant: 'secondary' },
  'stop-all': { icon: XIcon, variant: 'danger' },
  'restart-all': { icon: RefreshIcon, variant: 'secondary' },
  'logs-reset': { icon: TerminalIcon, variant: 'danger-quiet' },
  // Not `danger` either: it lands at each lane's next round boundary.
  engine: { icon: FlowIcon, variant: 'secondary' },
  // `danger`, like `clean-checkout`: it deletes stale worktrees, images and build cache.
  'system-prune': { icon: TrashIcon, variant: 'danger' },
};

export type CommandVerbCopy = VerbEntry & {
  readonly label: string;
  readonly title: string;
  readonly body: string;
  readonly cta: string;
};

const copyKeyOf = (verb: FleetCommandVerb): string => (verb === 'engine' ? 'engine-pin' : verb);

/**
 * One entry per verb read by both the footer button and its dialog's CTA
 * (BL — `stop-all`'s button and `stop-all`'s CTA must not drift), so both sites read
 * this same hook rather than copying strings inline.
 */
export function useCommandVerbCopy(verb: ConfirmableVerb): CommandVerbCopy {
  const t = useTranslations('pages.admin.fleet.command');
  const entry = VERB_TABLE[verb];

  return {
    ...entry,
    label: t(`${copyKeyOf(verb)}-label`),
    title: t(`${copyKeyOf(verb)}-title`),
    body: t(`${copyKeyOf(verb)}-body`),
    cta: t(`${copyKeyOf(verb)}-cta`),
  };
}

/** The activity feed's row label, over every verb — including the two that never confirm. */
export function useVerbLabel(verb: FleetCommandVerb): string {
  const t = useTranslations('pages.admin.fleet.command');

  return t(`${copyKeyOf(verb)}-label`);
}
